import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
} from 'react-bootstrap';
import axios from '../../services/axios';
import Loading from '../../components/Loading';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function PriorityList() {
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(false);

  // Controle do modal / edição
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPriority, setCurrentPriority] = useState(null);

  // Em vez de armazenar string "HH:MM", guardamos horas e minutos separadamente
  const [newPriority, setNewPriority] = useState({
    name: '',
    atendimentoHours: 0,
    atendimentoMinutes: 0,
    solucaoHours: 0,
    solucaoMinutes: 0,
  });

  // 1. Busca tudo da API (que retorna atendimentoVencimento: "HH:MM:SS")
  const fetchPriorities = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/priorities');
      setPriorities(response.data);
    } catch (error) {
      console.error('Erro ao carregar prioridades:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPriorities();
  }, []);

  // 2. Abre / fecha modal e limpa estado
  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setCurrentPriority(null);
    setNewPriority({
      name: '',
      atendimentoHours: 0,
      atendimentoMinutes: 0,
      solucaoHours: 0,
      solucaoMinutes: 0,
    });
  };

  // 3. Captura input (horas / minutos)
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Se o campo for o 'name', apenas atualize o valor como string
    if (name === 'name') {
      setNewPriority((prev) => ({
        ...prev,
        [name]: value,
      }));
      return; // Encerra a função aqui para o campo de nome
    }
    // Garante número inteiro e não string vazia
    const numeric = value === '' ? 0 : parseInt(value, 10);
    setNewPriority((prev) => ({
      ...prev,
      [name]: Number.isNaN(numeric) ? 0 : numeric,
    }));
  };

  // 4. Ao salvar, converte em "HH:MM:SS" e manda pra API
  const handleSavePriority = async () => {
    setLoading(true);
    try {
      // Converte horas/minutos em strings zero-padded
      const hAt = String(newPriority.atendimentoHours).padStart(2, '0');
      const mAt = String(newPriority.atendimentoMinutes % 60).padStart(2, '0');
      const atendimentoTime = `${hAt}:${mAt}:00`; // ex: "28:00:00" ou "60:30:00"

      const hSol = String(newPriority.solucaoHours).padStart(2, '0');
      const mSol = String(newPriority.solucaoMinutes % 60).padStart(2, '0');
      const solucaoTime = `${hSol}:${mSol}:00`;

      const payload = {
        name: newPriority.name,
        atendimentoVencimento: atendimentoTime,
        solucaoVencimento: solucaoTime,
      };

      if (editMode && currentPriority) {
        await axios.put(`/priorities/${currentPriority.id}`, payload);
      } else {
        await axios.post('/priorities', payload);
      }

      await fetchPriorities();
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar prioridade:', error);
    } finally {
      setLoading(false);
    }
  };

  // 5. Ao clicar em “Editar”, parseia “HH:MM:SS” em horas/minutos
  const handleEdit = (priority) => {
    // priority.atendimentoVencimento === "HH:MM:SS"
    const [hAtStr, mAtStr] = priority.atendimentoVencimento.split(':');
    const [hSolStr, mSolStr] = priority.solucaoVencimento.split(':');

    setCurrentPriority(priority);
    setNewPriority({
      name: priority.name,
      atendimentoHours: parseInt(hAtStr, 10),
      atendimentoMinutes: parseInt(mAtStr, 10),
      solucaoHours: parseInt(hSolStr, 10),
      solucaoMinutes: parseInt(mSolStr, 10),
    });
    setEditMode(true);
    handleShowModal();
  };

  // 6. Deleta prioridade
  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await axios.delete(`/priorities/${id}`);
      fetchPriorities();
    } catch (error) {
      console.error('Erro ao deletar prioridade:', error);
    } finally {
      setLoading(false);
    }
  };

  // 7. Função para exibir "HH:MM" no <Table>
  const formatTimeHHMM = (timeString) => {
    // timeString ex: "28:00:00" ou "05:30:00"
    const [h, m] = timeString.split(':');
    return `${h}:${m}`;
  };

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Breadcrumbs title="Prioridades" />

      <Card className="shadow-sm mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h1 className="page-heading m-0">Prioridades</h1>
          <Button variant="success" onClick={handleShowModal}>
            + Prioridade
          </Button>
        </Card.Header>

        <Card.Body>
          {loading && <Loading />}

          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Vencimento de atendimento</th>
                <th>Vencimento de solução</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {priorities.map((priority) => (
                <tr key={priority.id}>
                  <td>{priority.name}</td>
                  <td>{formatTimeHHMM(priority.atendimentoVencimento)}</td>
                  <td>{formatTimeHHMM(priority.solucaoVencimento)}</td>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleEdit(priority)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(priority.id)}
                    >
                      Remover
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Modal de criação / edição */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? 'Editar prioridade' : 'Adicionar prioridade'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={newPriority.name}
                onChange={handleInputChange}
                placeholder="Ex.: Alta, será resolvido em até 8h"
              />
            </Form.Group>

            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Vencimento de atendimento</Form.Label>
                  <Row>
                    <Col xs={6}>
                      <Form.Control
                        type="number"
                        name="atendimentoHours"
                        value={newPriority.atendimentoHours}
                        onChange={handleInputChange}
                        min={0}
                        placeholder="Horas (ex: 28)"
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Control
                        type="number"
                        name="atendimentoMinutes"
                        value={newPriority.atendimentoMinutes}
                        onChange={handleInputChange}
                        min={0}
                        max={59}
                        placeholder="Minutos (0–59)"
                      />
                    </Col>
                  </Row>
                  <Form.Text className="text-muted">
                    Informe o prazo total (horas e minutos). Ex: 28h → “28” e
                    “00”.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group>
                  <Form.Label>Vencimento de solução</Form.Label>
                  <Row>
                    <Col xs={6}>
                      <Form.Control
                        type="number"
                        name="solucaoHours"
                        value={newPriority.solucaoHours}
                        onChange={handleInputChange}
                        min={0}
                        placeholder="Horas (ex: 60)"
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Control
                        type="number"
                        name="solucaoMinutes"
                        value={newPriority.solucaoMinutes}
                        onChange={handleInputChange}
                        min={0}
                        max={59}
                        placeholder="Minutos (0–59)"
                      />
                    </Col>
                  </Row>
                  <Form.Text className="text-muted">
                    Informe o prazo total (horas e minutos). Ex: 60h → “60” e
                    “00”.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleSavePriority}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
